<script setup lang="ts">
/**
 * What is missing on a connected account and how to fix it: terms and KYC (Bucky's onboarding with BankID), signatures
 * from the board, or invalid information. Used by PersonOfInterestModal and by the route /accounts/:id/setup, which
 * platforms open in their own dialog.
 */
import { usePejer } from '@/Pejer'
import { ConnectedAccountsEmbed } from '@/Pejer/tables/ConnectedAccounts'
import DataStateButton from 'pejer/src/components/DataState/DataStateButton.vue'
import DataStateDisplay from 'pejer/src/components/DataState/DataStateDisplay.vue'
import HorizontalLine from 'pejer/src/components/HorizontalLine.vue'
import PejerTable from 'pejer/src/components/Table/PejerTable.vue'
import { Button } from 'pejer/src/components/ui/button'
import { DialogDescription, DialogTitle } from 'pejer/src/components/ui/dialog'
import { toast } from 'pejer/src/components/ui/toast'
import { PejerTablesList } from 'pejer/src/types/base'
import { SearchQuery } from 'pejer/src/types/types'
import usePejerMutation from 'pejer/src/usePejerMutation'
import usePejerQuery from 'pejer/src/usePejerQuery'
import { computed } from 'vue'
import PersonOfInterestSignatories from '../PersonOfInterest/PersonOfInterestSignatories.vue'
import OnboardingButton from './OnboardingButton.vue'

const props = withDefaults(
  defineProps<{
    connectedAccount: ConnectedAccountsEmbed.ListItem
    /** Rendered inside a Dialog: title and description use the dialog's own elements. */
    inDialog?: boolean
    /** The signatories are only fetched while the content is shown. */
    active?: boolean
  }>(),
  { inDialog: false, active: true }
)

const emit = defineEmits<{
  (e: 'close'): void
}>()

const titleTag = computed(() => (props.inDialog ? DialogTitle : 'h2'))
const descriptionTag = computed(() => (props.inDialog ? DialogDescription : 'div'))

const staticValues = computed<SearchQuery<PejerTablesList<'personsOfInterest'>>>(() => ({
  organizationId: {
    '=': {
      value: props.connectedAccount.organizationId,
    },
  },
  countryCode: {
    '=': {
      value: props.connectedAccount.countryCode,
    },
  },
}))

const onboardingStatus = computed(() => {
  if (props.connectedAccount.signatureCompletedAt) {
    if (!props.connectedAccount.payoutsEnabled) return 'Missing info'

    return 'Completed'
  }

  if (props.connectedAccount.tosAcceptedAt) return 'Missing signature'

  return 'Not completed'
})

const statusText = computed(() => {
  if (!props.connectedAccount.tosAcceptedAt) return 'Vilkår ikke akseptert'
  if (!props.connectedAccount.signatureCompletedAt) return 'Signatur mangler'
  return props.connectedAccount.statusInfo
})

const pejer = usePejer()

const { data, dataState } = usePejerQuery(pejer.urls.onboarding.getBusinessInformation, {
  pathArgs: {
    organizationID: props.connectedAccount.organizationId,
    countryCode: props.connectedAccount.countryCode,
  },
  queryKey: ['connectedAccounts', props.connectedAccount.connectedAccountID],
  enabled: computed(() => onboardingStatus.value === 'Missing signature' && props.active),
  select: (res) => {
    res.signatories = JSON.parse(res.signatories)
    return res
  },
})

const { mutate, dataState: mutationDataState } = usePejerMutation(
  pejer.urls.personsOfInterest.updateFromBrreg,
  {
    queryKeys: [['connectedAccounts'], ['businesses'], ['personsOfInterest']],
    onSuccess: () => {
      toast({
        title: 'Styret oppdatert',
        description: 'Styret er oppdatert fra Brønnøysundregisteret',
      })
    },
  }
)
</script>
<template>
  <component :is="titleTag" class="flex-1" :class="!inDialog && 'text-lg font-semibold'">
    <span v-if="onboardingStatus === 'Completed'">
      Endre informasjonen for {{ connectedAccount.businessName }}
    </span>
    <span v-else-if="onboardingStatus === 'Missing info'">
      Mangler gyldig informasjon for {{ connectedAccount.businessName }}
    </span>
    <span v-else-if="onboardingStatus === 'Missing signature'">
      Mangler signatur for {{ connectedAccount.businessName }}
    </span>
    <span v-else-if="onboardingStatus === 'Not completed'">
      Mangler opplysninger for {{ connectedAccount.businessName }}
    </span>
  </component>
  <component :is="descriptionTag" class="flex-1" :class="!inDialog && 'text-sm text-slate-500'">
    <span v-if="onboardingStatus === 'Completed'">
      Her kan du endre informasjonen for {{ connectedAccount.businessName }}.
    </span>
    <span v-else-if="onboardingStatus === 'Missing info'">
      Det mangler gyldig informasjon for {{ connectedAccount.businessName }}, vennligst gå gjennom og
      oppdater. <br />
      <span class="font-semibold text-red-500 italic">{{ statusText }}</span>
    </span>
    <div v-else-if="onboardingStatus === 'Missing signature'" class="flex justify-between">
      <span>
        Det mangler signatur for {{ connectedAccount.businessName }}, vennligst be vedkommende
        signere.
      </span>
      <div class="justify-end flex flex-col items-end text-right h-full">
        <span class="text-slate-500">Er styret utdatert?</span>
        <DataStateButton
          :data-state="mutationDataState"
          variant="outline"
          @click.prevent.stop="
            mutate({
              pathArgs: {
                countryCode: connectedAccount.countryCode,
                organizationId: connectedAccount.organizationId,
              },
            })
          "
        >
          Trykk her for å oppdatere fra brunnøysundregisteret
        </DataStateButton>
      </div>
    </div>
    <span v-else-if="onboardingStatus === 'Not completed'">
      Det mangler opplysninger for {{ connectedAccount.businessName }}, vennligst oppdater på linken
      under.
    </span>
  </component>

  <div v-if="onboardingStatus === 'Completed'">
    <OnboardingButton :connectedAccountID="connectedAccount.connectedAccountID" variant="default"
      >Gå til redigering</OnboardingButton
    >
  </div>
  <div v-else-if="onboardingStatus === 'Not completed'">
    <OnboardingButton :connectedAccountID="connectedAccount.connectedAccountID" />
  </div>
  <div class="gap-2 flex" v-else-if="onboardingStatus === 'Missing info'">
    <Button variant="outline" @click="emit('close')">Gå tilbake</Button>
    <OnboardingButton :connectedAccountID="connectedAccount.connectedAccountID" variant="default"
      >Rediger informasjon</OnboardingButton
    >
  </div>
  <div v-else class="shrink-1 overflow-y-auto">
    <PejerTable
      cannot-expand-rows
      table="personsOfInterest"
      urlKey="poi"
      :static-values="staticValues"
      :search-enabled="false"
      :options="{
        default_query: {
          sortBy: 'signatureRequired',
          sorter: 'Descending',
        },
      }"
      minimal-design
      show-all
      dont-fill-missing-entries
    >
      <template #table-title>
        <div class="flex gap-2 justify-between items-end">
          <DataStateDisplay v-bind="dataState">
            <PersonOfInterestSignatories
              :connected-account="connectedAccount"
              :has-valid-signatories="data.hasValidSigningCombination"
              :signatories="data.signatories"
            />
          </DataStateDisplay>
        </div>
        <HorizontalLine class="my-4 border-b" />
      </template>
    </PejerTable>
  </div>
</template>
